import { useState } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { hasGuestAgreement, acceptGuestAgreement } from '../engine/guestSession'
import BetaAgreementText from '../components/BetaAgreementText'

const NAVY = '#0F2A4A'
const GOLD = '#D4AF37'
const SILVER = '#64748B'

export default function BetaAgreement() {
  const { user, hasAcceptedAgreement, acceptBetaAgreement, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const guest = !user && searchParams.get('guest') === '1'
  const betaAgreementAccepted = guest ? hasGuestAgreement() : hasAcceptedAgreement
  const readOnly = searchParams.get('view') === '1'
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  if (!user && !guest && !readOnly) {
    return <Navigate to="/login" replace />
  }

  if (betaAgreementAccepted && !readOnly) {
    return <Navigate to={location.state?.from || '/'} replace />
  }

  const handleAccept = async () => {
    if (readOnly) return
    if (!checked) {
      setError('Please check the box to confirm you have read and agree to the Agreement.')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      if (guest) acceptGuestAgreement()
      else await acceptBetaAgreement()
      navigate(location.state?.from || '/', { replace: true })
    } catch (err) {
      setError(err.message || 'Something went wrong recording your acceptance. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    if (user) await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: NAVY,
      padding: '1.5rem',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 680,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        <div style={{ padding: '1.75rem 2rem 1rem' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: NAVY, letterSpacing: '-0.5px' }}>
            BE<span style={{ color: GOLD }}>Civitas</span>
          </div>
          <p style={{ color: SILVER, marginTop: 8, fontSize: 14 }}>
            {readOnly ? 'Read the current Participation Agreement. Viewing this page does not record acceptance.' : 'Before you continue, please review and accept the Participation Agreement below.'}
          </p>
        </div>

        <div style={{ padding: '0 2rem 1rem', overflowY: 'auto', flex: 1 }}>
          {guest && !readOnly && <p>No account is required. Your acceptance, district selection, and quiz answers stay in this browser. Clearing browser data removes them. Use manual district selection if you do not want to submit an address for lookup.</p>}
          <BetaAgreementText />
        </div>

        {error && (
          <div style={{
            margin: '0 2rem 1rem',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '0.75rem 1rem',
            color: '#991B1B',
            fontSize: 14,
          }}>
            {error}
          </div>
        )}

        <div style={{ padding: '1rem 2rem', borderTop: '1px solid #E2E8F0' }}>
          {readOnly ? (
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                padding: '0.7rem',
                borderRadius: 8,
                border: '1.5px solid #CBD5E1',
                backgroundColor: 'white',
                color: NAVY,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Return home
            </button>
          ) : (
            <>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: SILVER, marginBottom: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={event => setChecked(event.target.checked)}
                  style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                />
                I have read and agree to the BECivitas Participation Agreement.
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleDecline}
                  style={{
                    flex: 1,
                    padding: '0.7rem',
                    borderRadius: 8,
                    border: '1.5px solid #CBD5E1',
                    backgroundColor: 'white',
                    color: NAVY,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  {guest ? 'Decline' : 'Decline & sign out'}
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={submitting || !checked}
                  style={{
                    flex: 1,
                    padding: '0.7rem',
                    borderRadius: 8,
                    border: 'none',
                    backgroundColor: NAVY,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: (submitting || !checked) ? 'not-allowed' : 'pointer',
                    opacity: (submitting || !checked) ? 0.5 : 1,
                  }}
                >
                  {submitting ? 'Saving...' : 'I acknowledge and agree'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
